// Thin Luminary-flavoured wrapper over @learnforge/sdk/progress. The wrapper
// keeps the legacy luminary.* function names so scenes that import from
// '../progress' don't have to change.
//
// Legacy localStorage keys (`luminary.howtoplay.seen.v1` etc.) are read on
// demand so existing players don't lose their onboarding-seen state after the
// migration; new writes go through the @learnforge/sdk flag store.

import { createProgressStore } from '@learnforge/sdk/progress';
import type { LevelProgress, ProgressMap } from '@learnforge/sdk/progress';

export type { LevelProgress, ProgressMap };

const GAME_ID = 'luminary';

export const progressStore = createProgressStore(GAME_ID);

const LEGACY_HOWTOPLAY_KEY = `${GAME_ID}.howtoplay.seen.v1`;
const LEGACY_HOWTOPLAY_COLOR_KEY = `${GAME_ID}.howtoplay.color.seen.v1`;
const FLAG_HOWTOPLAY = 'howtoplay.seen';
const FLAG_HOWTOPLAY_COLOR = 'howtoplay.color.seen';

export function load(): ProgressMap {
  return progressStore.getAll();
}

export function markComplete(levelId: string, mirrorsUsed: number, timeMs: number): void {
  progressStore.markComplete(levelId, { bestMetric: mirrorsUsed, bestTimeMs: timeMs });
}

/**
 * Reads the stored "best mirrors used" for a level, mapping the generic
 * `bestMetric` field back to luminary's mirrors terminology.
 */
export function bestMirrors(levelId: string): number | undefined {
  return progressStore.get(levelId)?.bestMetric;
}

export function hasSeenHowToPlay(): boolean {
  if (progressStore.flag(FLAG_HOWTOPLAY)) return true;
  return readLegacyFlag(LEGACY_HOWTOPLAY_KEY);
}

export function markHowToPlaySeen(): void {
  progressStore.setFlag(FLAG_HOWTOPLAY, true);
}

export function hasSeenHowToPlayColor(): boolean {
  if (progressStore.flag(FLAG_HOWTOPLAY_COLOR)) return true;
  return readLegacyFlag(LEGACY_HOWTOPLAY_COLOR_KEY);
}

export function markHowToPlayColorSeen(): void {
  progressStore.setFlag(FLAG_HOWTOPLAY_COLOR, true);
}

export interface GameSettings {
  hintsEnabled: boolean;
}

const DEFAULT_SETTINGS: GameSettings = { hintsEnabled: true };

export function loadSettings(): GameSettings {
  return {
    hintsEnabled: progressStore.setting('hintsEnabled', DEFAULT_SETTINGS.hintsEnabled),
  };
}

export function saveSettings(settings: GameSettings): void {
  progressStore.setSetting('hintsEnabled', settings.hintsEnabled);
}

function readLegacyFlag(key: string): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}
